import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';
import { useToast } from 'components/hooks/useToast';

import { endpoints } from 'constants/endpoints';
import { isNetchexLogin } from 'constants/netChexConstants';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { NudgeCard } from './types/nudgeCard';

export const getNudgeCards = ({
  pageType = 'HOME_PAGE',
}: {
  pageType?: 'HOME_PAGE' | 'ONE_ON_ONE_PAGE';
}): Promise<{ entity: NudgeCard[] }> =>
  sendRequest({
    url: endpoints.workZero.nudgeCardsV3,
    method: 'GET',
    credentials: 'include',
    queryParams: {
      pageType,
    },
  });

export const useGetNudgeCardsQuery = ({ pageType = 'HOME_PAGE' }: { pageType?: 'HOME_PAGE' | 'ONE_ON_ONE_PAGE' }) =>
  useQuery(
    [REACT_QUERY_KEYS.meshRecommends.nudgeCards, pageType],
    () =>
      getNudgeCards({
        pageType,
      }).then((res) => res.entity),
    {
      refetchOnWindowFocus: 'always',
      enabled: !isNetchexLogin,
    }
  );

export const snoozeNudgeCard = (id: number, snoozeUpto: number, type: string) =>
  sendRequest({
    url: endpoints.workZero.snoozeNudgeCardV3,
    method: 'PUT',
    credentials: 'include',
    queryParams: {
      snooze_upto_time: snoozeUpto,
      type,
      id,
    },
  });

export const acknowledgeNudgeCard = ({ nudgeId }: { nudgeId: number }) =>
  sendRequest({
    url: endpoints.workZero.acknowledge,
    method: 'PUT',
    credentials: 'include',
    queryParams: {
      nudgeId,
    },
  });

export const useSnoozeNudgeCardMutation = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<
    unknown,
    unknown,
    { ecaId: number; nudgeId: number; snoozeUpto: number; type: string },
    { oldNudgeCards: NudgeCard[] }
  >(
    ({ ecaId, nudgeId, snoozeUpto, type = 'ECA_ID' }) =>
      snoozeNudgeCard(type === 'ECA_ID' ? ecaId : nudgeId, snoozeUpto, type),
    {
      onMutate: ({ ecaId }) => {
        const oldNudgeCards = queryClient.getQueryData<NudgeCard[]>([REACT_QUERY_KEYS.meshRecommends.nudgeCards]);
        queryClient.setQueryData<NudgeCard[]>([REACT_QUERY_KEYS.meshRecommends.nudgeCards], (nudgeCards) =>
          nudgeCards?.filter((nudgeCard) => nudgeCard.ecaId !== ecaId)
        );
        return { oldNudgeCards };
      },
      onSuccess: () => {
        toast({
          title: 'Successfully snoozed the nudge',
        });
        queryClient.invalidateQueries([REACT_QUERY_KEYS.meshRecommends.nudgeCards]);
      },
      onError: (_error, _variables, { oldNudgeCards }) => {
        toast({
          title: 'Failed to snoozed the nudge',
          status: 'error',
        });
        queryClient.setQueryData<NudgeCard[]>([REACT_QUERY_KEYS.meshRecommends.nudgeCards], oldNudgeCards);
      },
    }
  );
};
